import { Injectable, inject } from '@angular/core'
import { HttpResponse, type HttpInterceptorFn } from '@angular/common/http'
import { Subject, type Observable } from 'rxjs'
import { tap } from 'rxjs/operators'
import { environment } from '../../../environments/environment'

/**
 * Announces that a campaign draft was written to (#3646), so the LinkedIn
 * forecast card can refetch after every save instead of each save site
 * wiring its own refresh. Carries the draft id.
 */
@Injectable({ providedIn: 'root' })
export class DraftWriteEvents {
  private readonly subject = new Subject<number>()
  readonly writes: Observable<number> = this.subject.asObservable()

  emit(draftId: number): void {
    this.subject.next(draftId)
  }
}

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])
const API_PREFIX = environment.apiBase.replace(/[.*+?^$()|[\]\\{}]/g, '\\$&')
const DRAFT_URL = new RegExp(`^${API_PREFIX}/(?:admin|portal)/campaign-drafts/(\\d+)(/.*)?$`)

/** Writes that do not change what the forecast is computed from. */
function isNonTargetingWrite(method: string, rest: string): boolean {
  if (rest === '' && method === 'DELETE') return true // discard the draft
  if (rest === '/submit' || rest === '/keyword-ideas') return true
  if (rest === '/step2/ai-suggestions') return true // suggestions only, nothing is saved
  return rest === '/images' || rest.startsWith('/images/')
}

export const draftWriteInterceptor: HttpInterceptorFn = (req, next) => {
  if (READ_METHODS.has(req.method)) return next(req)
  const match = DRAFT_URL.exec(req.url.split('?')[0])
  if (!match) return next(req)
  const rest = match[2] ?? ''
  if (isNonTargetingWrite(req.method, rest)) return next(req)
  const events = inject(DraftWriteEvents)
  const draftId = Number(match[1])
  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) events.emit(draftId)
    })
  )
}
